/**
 * Static fallback content for the Home page.
 * Used only when the CMS API is unavailable or returns no data.
 * Image keys are resolved via cmsAssets.js.
 */
export const homeFallbackContent = {
  hero: {
    headline:
      "Building A Skilled India Through\nEngineering, Innovation &\nSustainable Infrastructure",
    subtitle:
      "Engineering drives progress, but many talented engineers need the right support to succeed. ASSET is bringing together students, engineers, industries, institutions, and mentors to build a stronger engineering ecosystem—one that helps people grow, innovate, and contribute to India's future.",
    ctaText: "Partner With ASSET",
    ctaLink: "/contact-us",
    secondaryCtaText: "Join the Movement",
    secondaryCtaLink: "#join-movement",
    imageKey: "image_hero",
  },
  partner_logos: {
    row1: [
      { logoKey: "partner_logo_cambridge", alt: "Cambridge Institute of Technology" },
      { logoKey: "partner_logo_bms", alt: "BMS Institute of Technology and Management" },
      { logoKey: "partner_logo_dayanand", alt: "Dayananda Sagar College of Engineering" },
      { logoKey: "partner_logo_ciet", alt: "CIET College" },
      { logoKey: "partner_logo_kns", alt: "KNS Institute of Technology" },
      { logoKey: "partner_logo_bangalore_inst", alt: "Bangalore Institute of Technology" },
      { logoKey: "partner_logo_gm", alt: "GM Institute of Technology" },
      { logoKey: "partner_logo_bnm", alt: "BNM Institute of Technology" },
    ],
    row2: [
      { logoKey: "partner_logo_srm", alt: "SRM Institute of Science and Technology" },
      { logoKey: "partner_logo_presidency", alt: "Presidency University" },
      { logoKey: "partner_logo_ranganathar", alt: "Sri Ranganathar Institute of Polytechnic College" },
      { logoKey: "partner_logo_bangalore_inst", alt: "Bangalore Institute of Technology" },
      { logoKey: "partner_logo_sea", alt: "S.E.A Group of Institutions" },
      { logoKey: "partner_logo_rvs", alt: "RVS College" },
      { logoKey: "partner_logo_sir_m", alt: "Sir M Visvesvaraya Institute of Technology" },
      { logoKey: "partner_logo_sri_shakti", alt: "Sri Shakthi Institute of Engineering and Technology" },
      { logoKey: "partner_logo_tagore", alt: "Tagore Institute of Engineering and Technology" },
    ],
  },
  homepage_stats: {
    items: [
      { stat: "3", description: "Mega Job Fairs" },
      {
        stat: "50+",
        description:
          "Awareness programs (HR Events, Industry Seminars, Academia Seminars)",
      },
      { stat: "2000+", description: "Career Opportunities" },
      { stat: "50+", description: "Campus Outreach" },
    ],
  },
  about_preview: {
    title: "Why ASSET Exists",
    subtitle: "Because Engineering Talent Deserves Better Opportunities",
    paragraphs: [
      "India has many talented engineers, but many struggle to gain industry exposure, practical skills, and career opportunities.",
      "ASSET bridges the gap between education and industry by connecting engineers with learning opportunities, mentorship, industry experts, and professional networks. We help engineers learn, grow, and succeed while supporting industries with skilled and future-ready talent.",
    ],
    closingStatement:
      "When engineers grow, industries grow. When industries grow, nations prosper.",
    badgeText: "Built Over 3 Years, Growing Into the 4th",
    productivityStat: "27%",
    productivityLabel: "Productivity increase on average",
    imageKey: "asset_about",
    sealImageKey: "wesince",
  },
  impact_areas: {
    title: "Our Impact Areas",
    items: [
      {
        imageKey: "our_impact",
        iconKey: "impact_icon_hammer",
        alt: "Engineering team collaborating around laptops and whiteboards",
        title: "Engineering Skill Development",
        description:
          "Helping engineers build practical skills that complement academic learning and improve industry readiness.",
      },
      {
        imageKey: "our_impact_1",
        iconKey: "impact_icon_trend",
        alt: "Professional reviewing career pathway materials",
        title: "Career Growth & Employability",
        description:
          "Providing guidance, mentorship, and opportunities that help engineers build successful careers.",
      },
      {
        imageKey: "our_impact_2",
        iconKey: "impact_icon_collab",
        alt: "Students and trainees at an industrial site visit",
        title: "Industry-Academia Collaboration",
        description:
          "Connecting educational institutions with industry to create stronger learning and career pathways.",
      },
      {
        imageKey: "our_impact_3",
        iconKey: "impact_icon_bulb",
        alt: "Engineer working on a 3D building model on a computer",
        title: "Innovation & Future Technologies",
        description:
          "Promoting awareness of emerging technologies, digital transformation, sustainability, and innovation.",
      },
      {
        imageKey: "our_impact_4",
        iconKey: "impact_icon_people",
        alt: "Students working together in a hands-on engineering workshop",
        title: "Engineering Community Development",
        description:
          "Building stronger engineering networks through collaboration, mentorship, and community-driven learning.",
      },
      {
        imageKey: "our_impact_5",
        iconKey: "impact_icon_globe",
        alt: "Engineer monitoring industrial robotics with the Indian flag",
        title: "National Engineering Growth",
        description:
          "Supporting initiatives that strengthen India's engineering capabilities and future workforce.",
      },
    ],
  },
  partnership: {
    title: "Why Partner With ASSET",
    ctaText: "Partner With Us",
    ctaLink: "/contact-us",
    cards: [
      {
        imageKey: "meaningful",
        title: "Meaningful Social Impact",
        description:
          "Support initiatives that directly improve employability and livelihoods.",
      },
      {
        imageKey: "crs",
        title: "CSR Programs with Impact",
        description:
          "Aligned with skill development, women empowerment, and sustainability goals.",
      },
      {
        imageKey: "industry",
        title: "Industry-Relevant Programs",
        description:
          "Designed with insights from professionals and domain experts.",
      },
      {
        imageKey: "accountability",
        title: "Governance and Transparency",
        description:
          "Clear reporting, structured execution, and financial accountability.",
      },
    ],
  },
  who_we_serve: {
    title: "Who We Are Supporting Every Stage of the Engineering Journey",
    slides: [
      {
        imageKey: "serve_frame1",
        title: "Students",
        description: "Gain industry exposure, skills, and career guidance.",
        alt: "Students in a classroom setting",
      },
      {
        imageKey: "serve_frame4",
        title: "Graduates & Job Seekers",
        description: "Gain confidence, skills, and career opportunities.",
        alt: "Graduates in caps and gowns",
      },
      {
        imageKey: "serve_frame3",
        title: "Working Professionals",
        description: "Build skills, network, and grow your engineering career.",
        alt: "Working professionals collaborating",
      },
      {
        imageKey: "serve_frame2",
        title: "Educational Institutions",
        description: "Partner with industry to support future engineers.",
        alt: "Educational institution partnership",
      },
      {
        imageKey: "emerging_workforce",
        title: "Emerging Workforce",
        description: "Stay updated, build leadership, and grow your network.",
        alt: "Emerging workforce professionals",
      },
    ],
  },
  programs_overview: {
    title: "Programs Overview",
    subtitle: "Building Engineers. Strengthening Industries.",
    description:
      "Our initiatives are designed to support engineers throughout their professional journey.",
    ctaText: "Explore All Programs",
    ctaLink: "/programs",
    items: [
      {
        number: "1",
        title: "Engineering Skill Development Program",
        description:
          "Practical learning, industry readiness, technical awareness, and professional growth.",
      },
      {
        number: "2",
        title: "Career Guidance & Employability Support",
        description:
          "Creating stronger connections between institutions and industry.",
      },
      {
        number: "3",
        title: "Independent Engineer Initiative",
        description:
          "Supporting engineers in consulting, freelancing, and entrepreneurship.",
      },
      {
        number: "4",
        title: "Engineer Welfare & Professional Recognition",
        description: "Encouraging leadership, networking, and development.",
      },
      {
        number: "5",
        title: "Industry–Academia Collaboration",
        description:
          "Creating stronger connections between institutions and industry.",
      },
      {
        number: "6",
        title: "Technology & Innovation Development",
        description: "Helping engineers grow with changing technologies.",
      },
      {
        number: "7",
        title: "Engineering Community Development",
        description:
          "Connecting engineers through collaboration and networking.",
      },
      {
        number: "8",
        title: "India Engineering Growth Mission",
        description:
          "Supporting India's growth through engineering and innovation.",
      },
    ],
  },
  industry_partnerships: {
    title: "Industry & Institutional Partnerships",
    subtitle: "Building the Future Together",
    paragraphs: [
      "Creating a stronger engineering ecosystem requires collaboration.",
      "ASSET works with industries, educational institutions, technology partners, CSR organizations, and government stakeholders to develop engineering talent, strengthen workforce readiness, and promote innovation.",
      "Whether through mentorship programs, workshops, industry engagement, internships, or technology awareness initiatives, our partnerships create meaningful opportunities for future engineers while supporting industry growth.",
    ],
    ctaText: "Partner With ASSET",
    ctaLink: "/contact-us",
    images: {
      top: {
        imageKey: "industry_collage_top",
        alt: "Diverse team stacking hands over a meeting table",
      },
      bottomLeft: {
        imageKey: "industry_collage_bl",
        alt: "Professionals discussing strategy in an office",
      },
      bottomRight: {
        imageKey: "industry_collage_br",
        alt: "Engineer placing a hard hat on a smiling child",
      },
    },
  },
  join_movement: {
    eyebrow: "Join the Movement",
    title: "Be Part of India's Engineering Future",
    paragraphs: [
      "Whether you're a student, engineer, educator, mentor, employer, or industry leader, there's a place for you in the ASSET community.",
      "Together, we can create more opportunities, strengthen engineering talent, encourage innovation, and contribute to India's growth.",
    ],
    primaryCtaText: "Partner With ASSET",
    primaryCtaLink: "/contact-us",
    secondaryCtaText: "Become a Member",
    secondaryCtaLink: "/contact-us",
    imageKey: "join_movement_hero",
    imageAlt: "ASSET team celebrating together around a laptop",
  },
  join_us: {
    title: "Join the movement and help shape the future of engineering.",
    placeholder: "Type your email here, we'll Contact You!",
    buttonText: "Send",
  },
};

export default homeFallbackContent;
